import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { toastError } from '@components/Toast';

import BrandWiseSizeCategoryDropdown from '@features/measurement/components/BrandWiseSizeCategoryDropdown';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import {
	fetchProductWiseMeasurementSizeByCategoryId,
	removeMeasurementSize,
	saveNewSizeInMeasurementTable,
	saveProductMeasurementChangeLog
} from '@store/actions/MeasurementAction';
import { getPomAndSize, useMeasurement } from '@store/reducers';
import { Types } from '@store/types';

import { increaseSizeCategorySequence, removeSizeFromProduct } from '@services/measurementService';

const AddMeasurementSize = ({ isEditable }) => {
	const [pomAndSize, setPomAndSize] = useState(null);
	const pomAndSizeList = useSelector(getPomAndSize);
	const [selectedSizeIds, setSelectedSizeIds] = useState([]);
	const fitWiseSizeList = useMeasurement()?.fitWiseSizeList || {};
	const sizeCategorySequence = useMeasurement()?.sizeCategorySequence || {};
	const initialMeasurementTableData = useMeasurement()?.initialMeasurementTableData || [];
	const initMeasurementId = useMeasurement()?.initId;
	const dispatch = useDispatch();
	const selectedMeasurementType = pomAndSizeList?.measurementInfo?.measurementType.constant;
	const { id } = useParams();

	useEffect(() => {
		if (pomAndSizeList?.sizeCategoryId) {
			(async () => {
				await renderMeasurementSizes();
			})();
		}
	}, [pomAndSizeList?.sizeCategoryId]);

	useEffect(() => {
		setPomAndSize(pomAndSizeList);
	}, [pomAndSizeList]);

	useEffect(() => {
		let sizeIds = [];
		for (let item in fitWiseSizeList) {
			fitWiseSizeList[item].sizeList.map((size) => {
				sizeIds.push(size.id);
			});
		}
		setSelectedSizeIds([...new Set(sizeIds)]);
	}, [fitWiseSizeList]);

	const renderMeasurementSizes = async () => {
		try {
			dispatch(openLoader());
			await dispatch(fetchProductWiseMeasurementSizeByCategoryId(pomAndSizeList.sizeCategoryId));
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const sortBySerial = (arrayList) => {
		if (arrayList) {
			return arrayList.slice().sort((a, b) => a.serial - b.serial);
		}
	};
	const allSizesList = sortBySerial(
		pomAndSize?.sizeCategory?.productSizeList?.filter((item) => !item.isDisabled)
	);

	const onSizeClick = async (obj) => {
		if (['NITEX_DEVELOPMENT', 'BUYER_FIT_DEVELOPMENT'].includes(selectedMeasurementType)) {
			if (selectedSizeIds.includes(obj.id)) return;
			let changeLogInfo = {
				changeLogType: selectedSizeIds.length > 0 ? 'SIZE_REPLACE' : 'SIZE_ADD',
				dto: {
					sizeId: obj.id
				},
				id: initialMeasurementTableData?.[0]?.id || initMeasurementId
			};
			if (selectedSizeIds.includes(obj.id)) {
				changeLogInfo.dto = {
					...changeLogInfo.dto,
					oldSizeId: selectedSizeIds[0]
				};
			}
			dispatch(saveProductMeasurementChangeLog(changeLogInfo));
			let payload = {};
			let newSizeList = {};
			if (Object.keys(fitWiseSizeList).length > 0) {
				for (let item in fitWiseSizeList) {
					newSizeList[item] = {
						baseSize: obj.id,
						sizeList: [obj]
					};
				}
			} else {
				newSizeList['FIRST_FIT'] = {
					baseSize: obj.id,
					sizeList: [obj]
				};
			}

			payload.isNeedToMerge = false;
			payload.fitWiseSizeList = newSizeList;
			payload.sizeValueList = [{ ...obj, value: '' }];
			dispatch(saveNewSizeInMeasurementTable(payload));
		} else {
			if (selectedSizeIds.includes(obj.id)) {
				try {
					dispatch(openLoader());
					const data = await removeSizeFromProduct({
						productId: id,
						productSizeId: obj.id,
						measurementType: selectedMeasurementType,
						isInstantRemoved: false
					});
					if (data === true) {
						let changeLogInfo = {
							changeLogType: 'SIZE_REMOVE',
							dto: {
								sizeId: obj.id
							}
						};
						await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
						await dispatch(removeMeasurementSize({ sizeId: obj.id }));
					}
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
					toastError('Failed to Remove size');
				}
			} else {
				if (!([obj.sizeCategoryId] in sizeCategorySequence)) {
					try {
						dispatch(openLoader());
						const data = await increaseSizeCategorySequence(
							id,
							obj.sizeCategoryId,
							selectedMeasurementType
						);
						dispatch({
							type: Types.ADD_MEASUREMENT_SIZE_CATEGORY_SEQUENCE,
							payload: data
						});
						dispatch(closeLoader());
					} catch {
						dispatch(closeLoader());
					}
				}

				let changeLogInfo = {
					changeLogType: 'SIZE_ADD',
					dto: {
						sizeId: obj.id
					},
					id: initialMeasurementTableData?.[0]?.id || initMeasurementId
				};
				if (selectedSizeIds.includes(obj.id)) {
					changeLogInfo.dto = {
						...changeLogInfo.dto
					};
				}
				dispatch(saveProductMeasurementChangeLog(changeLogInfo));
				let payload = {};
				let newSizeList = {};
				if (Object.keys(fitWiseSizeList).length > 0) {
					for (let item in fitWiseSizeList) {
						newSizeList[item] = {
							baseSize: fitWiseSizeList[item].baseSize,
							sizeList: [...fitWiseSizeList[item].sizeList, obj],
							fitTypeId: fitWiseSizeList[item].fitTypeId
						};
					}
				} else {
					newSizeList['FIRST_FIT'] = {
						baseSize: undefined,
						fitTypeId: initialMeasurementTableData?.[0]?.id || initMeasurementId,
						sizeList: [obj]
					};
				}

				payload.isNeedToMerge = true;
				payload.fitWiseSizeList = newSizeList;
				payload.sizeValueList = [{ ...obj, value: '' }];
				dispatch(saveNewSizeInMeasurementTable(payload));
			}
		}
	};

	return (
		<>
			<BrandWiseSizeCategoryDropdown disabled={!isEditable()} onAddSuccess={renderMeasurementSizes} />

			<div className="select-sizes">
				<div className="all-sizes">
					{allSizesList?.map((item, index) => {
						return (
							<span
								key={index}
								className={selectedSizeIds.includes(item.id) ? 'size selected' : 'size'}
								onClick={() => {
									if (isEditable()) onSizeClick(item);
								}}
							>
								{item.label}
							</span>
						);
					})}
				</div>
			</div>
		</>
	);
};

export default AddMeasurementSize;
